import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Wrench,
    RefreshCw,
    Users,
    FileText,
    AlertTriangle,
    CheckCircle,
    Database,
    ArrowRight,
    Trash2,
    Zap,
    Trophy,
    LayoutGrid
} from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAdmin } from '../contexts/AdminContext';
import {
    getUserStatsDiscrepancies,
    reconcileUserStats,
    getPostStatsDiscrepancies,
    reconcilePostStats,
    syncUsernameCache,
    getBasketballCacheStatus,
    clearAllBasketballCache,
    clearAllTeamsCache,
    clearAllGamesCache,
    clearStandingsCache,
    clearLiveGamesCache,
    clearCompetitionsCache
} from '../utils/api';
import { formatNumber } from '../utils/helpers';
import toast from 'react-hot-toast';

// Discrepancy Card Component
function DiscrepancyCard({ title, icon: Icon, discrepancies, onFix, isFixing, type }) {
    const hasIssues = discrepancies?.totalWithIssues > 0;
    const items = discrepancies?.discrepancies || [];

    return (
        <Card className="h-full">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${hasIssues ? 'bg-yellow-500/10' : 'bg-green/10'}`}>
                        <Icon size={20} className={hasIssues ? 'text-yellow-500' : 'text-green'} />
                    </div>
                    <div>
                        <h3 className="font-semibold">{title}</h3>
                        <p className="text-sm text-muted-light dark:text-muted-dark">
                            Checked: {discrepancies?.totalChecked || 0} • Issues: {discrepancies?.totalWithIssues || 0}
                        </p>
                    </div>
                </div>
                {hasIssues && (
                    <Button
                        variant="primary"
                        size="small"
                        onClick={onFix}
                        loading={isFixing}
                    >
                        Fix All
                    </Button>
                )}
            </div>

            {items.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                    {items.slice(0, 10).map((item, idx) => (
                        <div
                            key={idx}
                            className="p-3 bg-accent-light dark:bg-accent-dark rounded-lg text-sm"
                        >
                            {type === 'user' ? (
                                <>
                                    <p className="font-medium">@{item.username}</p>
                                    <div className="flex gap-4 mt-1 text-xs text-muted-light dark:text-muted-dark">
                                        <span>Posts: {item.stored.posts} → {item.actual.posts}</span>
                                        <span>Followers: {item.stored.followers} → {item.actual.followers}</span>
                                        <span>Following: {item.stored.following} → {item.actual.following}</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="font-medium font-mono text-xs">{item.postId}</p>
                                    <div className="flex gap-4 mt-1 text-xs text-muted-light dark:text-muted-dark">
                                        <span>↑ {item.stored.upvotes} → {item.actual.upvotes}</span>
                                        <span>↓ {item.stored.downvotes} → {item.actual.downvotes}</span>
                                        <span>💬 {item.stored.comments} → {item.actual.comments}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                    {items.length > 10 && (
                        <p className="text-center text-sm text-muted-light dark:text-muted-dark">
                            And {items.length - 10} more...
                        </p>
                    )}
                </div>
            ) : (
                <div className="flex items-center justify-center py-8 text-green">
                    <CheckCircle size={20} className="mr-2" />
                    <span>All stats are accurate</span>
                </div>
            )}
        </Card>
    );
}

export default function Maintenance() {
    const { admin } = useAdmin();
    const queryClient = useQueryClient();
    const isSuperAdmin = admin?.privileges?.includes('super_admin');

    // User stats discrepancies
    const { data: userDiscrepancies, isLoading: loadingUserDiscrepancies, refetch: refetchUserDiscrepancies } = useQuery({
        queryKey: ['user-stats-discrepancies'],
        queryFn: () => getUserStatsDiscrepancies(200),
        enabled: isSuperAdmin,
        staleTime: 0
    });

    // Post stats discrepancies
    const { data: postDiscrepancies, isLoading: loadingPostDiscrepancies, refetch: refetchPostDiscrepancies } = useQuery({
        queryKey: ['post-stats-discrepancies'],
        queryFn: () => getPostStatsDiscrepancies(200),
        enabled: isSuperAdmin,
        staleTime: 0
    });

    // Reconcile user stats mutation
    const reconcileUsersMutation = useMutation({
        mutationFn: (userIds) => reconcileUserStats(userIds),
        onSuccess: (data) => {
            toast.success(data?.message || 'User stats reconciled');
            refetchUserDiscrepancies();
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to reconcile user stats');
        }
    });

    // Reconcile post stats mutation
    const reconcilePostsMutation = useMutation({
        mutationFn: (postIds) => reconcilePostStats(postIds),
        onSuccess: (data) => {
            toast.success(data?.message || 'Post stats reconciled');
            refetchPostDiscrepancies();
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to reconcile post stats');
        }
    });

    // Sync username cache mutation
    const syncUsernameMutation = useMutation({
        mutationFn: syncUsernameCache,
        onSuccess: (data) => {
            toast.success(data?.message || 'Username cache synced');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to sync username cache');
        }
    });

    const handleFixUserStats = () => {
        const userIds = userDiscrepancies?.data?.discrepancies?.map(d => d.userId) || [];
        if (userIds.length > 0) {
            reconcileUsersMutation.mutate(userIds);
        }
    };

    const handleFixPostStats = () => {
        const postIds = postDiscrepancies?.data?.discrepancies?.map(d => d.postId) || [];
        if (postIds.length > 0) {
            reconcilePostsMutation.mutate(postIds);
        }
    };

    const handleRefreshAll = () => {
        refetchUserDiscrepancies();
        refetchPostDiscrepancies();
        refetchCacheStatus();
    };

    // Basketball Cache Status
    const { data: cacheStatus, isLoading: loadingCacheStatus, refetch: refetchCacheStatus } = useQuery({
        queryKey: ['basketball-cache-status'],
        queryFn: getBasketballCacheStatus,
        enabled: isSuperAdmin,
        staleTime: 30000
    });

    // Cache clearing mutations
    const clearAllCacheMutation = useMutation({
        mutationFn: clearAllBasketballCache,
        onSuccess: () => {
            toast.success('All basketball cache cleared');
            refetchCacheStatus();
        },
        onError: (error) => toast.error(error.message || 'Failed to clear cache')
    });

    const clearTeamsCacheMutation = useMutation({
        mutationFn: clearAllTeamsCache,
        onSuccess: () => toast.success('All team caches cleared (rosters, details, etc.)'),
        onError: (error) => toast.error(error.message || 'Failed to clear teams cache')
    });

    const clearGamesCacheMutation = useMutation({
        mutationFn: clearAllGamesCache,
        onSuccess: () => toast.success('All game caches cleared'),
        onError: (error) => toast.error(error.message || 'Failed to clear games cache')
    });

    const clearStandingsCacheMutation = useMutation({
        mutationFn: clearStandingsCache,
        onSuccess: () => toast.success('Standings cache cleared'),
        onError: (error) => toast.error(error.message || 'Failed to clear standings cache')
    });

    const clearLiveGamesCacheMutation = useMutation({
        mutationFn: clearLiveGamesCache,
        onSuccess: () => toast.success('Live games cache cleared'),
        onError: (error) => toast.error(error.message || 'Failed to clear live games cache')
    });

    const clearCompetitionsCacheMutation = useMutation({
        mutationFn: clearCompetitionsCache,
        onSuccess: () => toast.success('Competitions cache cleared'),
        onError: (error) => toast.error(error.message || 'Failed to clear competitions cache')
    });

    if (!isSuperAdmin) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <AlertTriangle size={48} className="text-yellow-500 mb-4" />
                <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                <p className="text-muted-light dark:text-muted-dark">
                    Only super admins can access maintenance tools.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                        <Wrench size={32} />
                        Maintenance
                    </h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        System maintenance and data reconciliation tools
                    </p>
                </div>
                <Button
                    variant="secondary"
                    onClick={handleRefreshAll}
                    disabled={loadingUserDiscrepancies || loadingPostDiscrepancies}
                >
                    <RefreshCw size={18} className={loadingUserDiscrepancies || loadingPostDiscrepancies ? 'animate-spin' : ''} />
                    Refresh All
                </Button>
            </div>

            {/* Warning Banner */}
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                        <p className="font-medium text-yellow-600 dark:text-yellow-400">
                            Admin Maintenance Tools
                        </p>
                        <p className="text-sm text-muted-light dark:text-muted-dark mt-1">
                            These tools are for fixing data inconsistencies that may occur during development or edge cases.
                            Use with caution in production.
                        </p>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Sync Username Cache */}
                <Card className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue/10">
                            <Database size={20} className="text-blue" />
                        </div>
                        <div>
                            <h3 className="font-semibold">Username Cache</h3>
                            <p className="text-sm text-muted-light dark:text-muted-dark">
                                Sync Redis with database
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => syncUsernameMutation.mutate()}
                        loading={syncUsernameMutation.isPending}
                    >
                        Sync Now
                    </Button>
                </Card>

                {/* Stats Summary */}
                <Card className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple/10">
                        <Users size={20} className="text-purple" />
                    </div>
                    <div>
                        <h3 className="font-semibold">User Stats Issues</h3>
                        <p className="text-2xl font-bold">
                            {loadingUserDiscrepancies ? '...' : (userDiscrepancies?.data?.totalWithIssues || 0)}
                        </p>
                    </div>
                </Card>

                <Card className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange/10">
                        <FileText size={20} className="text-orange" />
                    </div>
                    <div>
                        <h3 className="font-semibold">Post Stats Issues</h3>
                        <p className="text-2xl font-bold">
                            {loadingPostDiscrepancies ? '...' : (postDiscrepancies?.data?.totalWithIssues || 0)}
                        </p>
                    </div>
                </Card>
            </div>

            {/* Basketball Cache Management */}
            <Card>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange/10">
                            <Trophy size={20} className="text-orange" />
                        </div>
                        <div>
                            <h3 className="font-semibold">Basketball API Cache</h3>
                            <p className="text-sm text-muted-light dark:text-muted-dark">
                                Clear cached data to force fresh fetches from the database
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {loadingCacheStatus ? (
                            <span className="text-sm text-muted-light dark:text-muted-dark">Loading...</span>
                        ) : cacheStatus?.data?.connected ? (
                            <span className="flex items-center gap-1 text-sm text-green">
                                <CheckCircle size={14} />
                                Redis Connected
                            </span>
                        ) : (
                            <span className="flex items-center gap-1 text-sm text-yellow-500">
                                <AlertTriangle size={14} />
                                {cacheStatus?.data?.enabled ? 'Disconnected' : 'Cache Disabled'}
                            </span>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {/* Clear All Teams */}
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => clearTeamsCacheMutation.mutate()}
                        loading={clearTeamsCacheMutation.isPending}
                        className="flex flex-col items-center gap-1 py-3 h-auto"
                    >
                        <Users size={18} />
                        <span className="text-xs">All Teams</span>
                        <span className="text-[10px] text-muted-light dark:text-muted-dark">Rosters, Details</span>
                    </Button>

                    {/* Clear All Games */}
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => clearGamesCacheMutation.mutate()}
                        loading={clearGamesCacheMutation.isPending}
                        className="flex flex-col items-center gap-1 py-3 h-auto"
                    >
                        <LayoutGrid size={18} />
                        <span className="text-xs">All Games</span>
                        <span className="text-[10px] text-muted-light dark:text-muted-dark">Actions, Box Scores</span>
                    </Button>

                    {/* Clear Standings */}
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => clearStandingsCacheMutation.mutate()}
                        loading={clearStandingsCacheMutation.isPending}
                        className="flex flex-col items-center gap-1 py-3 h-auto"
                    >
                        <Trophy size={18} />
                        <span className="text-xs">Standings</span>
                        <span className="text-[10px] text-muted-light dark:text-muted-dark">League Tables</span>
                    </Button>

                    {/* Clear Live Games */}
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => clearLiveGamesCacheMutation.mutate()}
                        loading={clearLiveGamesCacheMutation.isPending}
                        className="flex flex-col items-center gap-1 py-3 h-auto"
                    >
                        <Zap size={18} />
                        <span className="text-xs">Live Games</span>
                        <span className="text-[10px] text-muted-light dark:text-muted-dark">Live List</span>
                    </Button>

                    {/* Clear Competitions */}
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => clearCompetitionsCacheMutation.mutate()}
                        loading={clearCompetitionsCacheMutation.isPending}
                        className="flex flex-col items-center gap-1 py-3 h-auto"
                    >
                        <Database size={18} />
                        <span className="text-xs">Competitions</span>
                        <span className="text-[10px] text-muted-light dark:text-muted-dark">Leagues, Seasons</span>
                    </Button>

                    {/* Clear All */}
                    <Button
                        variant="danger"
                        size="small"
                        onClick={() => {
                            if (window.confirm('Clear ALL basketball cache? This will force re-fetch of all data.')) {
                                clearAllCacheMutation.mutate();
                            }
                        }}
                        loading={clearAllCacheMutation.isPending}
                        className="flex flex-col items-center gap-1 py-3 h-auto"
                    >
                        <Trash2 size={18} />
                        <span className="text-xs">Clear All</span>
                        <span className="text-[10px] opacity-80">Everything</span>
                    </Button>
                </div>

                <p className="mt-4 text-xs text-muted-light dark:text-muted-dark">
                    💡 <strong>Tip:</strong> After syncing game data via Convex, clear the relevant cache to see updates immediately in the app.
                    Team roster cache (1 hour), game cache (5 min), standings (5 min).
                </p>
            </Card>

            {/* Discrepancy Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {loadingUserDiscrepancies ? (
                    <Card className="flex items-center justify-center h-64">
                        <LoadingSpinner />
                    </Card>
                ) : (
                    <DiscrepancyCard
                        title="User Stats"
                        icon={Users}
                        discrepancies={userDiscrepancies?.data}
                        onFix={handleFixUserStats}
                        isFixing={reconcileUsersMutation.isPending}
                        type="user"
                    />
                )}

                {loadingPostDiscrepancies ? (
                    <Card className="flex items-center justify-center h-64">
                        <LoadingSpinner />
                    </Card>
                ) : (
                    <DiscrepancyCard
                        title="Post Stats"
                        icon={FileText}
                        discrepancies={postDiscrepancies?.data}
                        onFix={handleFixPostStats}
                        isFixing={reconcilePostsMutation.isPending}
                        type="post"
                    />
                )}
            </div>

            {/* Info Section */}
            <Card>
                <h3 className="font-semibold mb-4">What gets reconciled?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                    <div>
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                            <Users size={16} />
                            User Stats
                        </h4>
                        <ul className="space-y-1 text-muted-light dark:text-muted-dark">
                            <li>• Posts count (non-deleted posts by user)</li>
                            <li>• Followers count (actual follows)</li>
                            <li>• Following count (actual follows)</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                            <FileText size={16} />
                            Post Stats
                        </h4>
                        <ul className="space-y-1 text-muted-light dark:text-muted-dark">
                            <li>• Upvotes count (actual upvotes)</li>
                            <li>• Downvotes count (actual downvotes)</li>
                            <li>• Score (upvotes - downvotes)</li>
                            <li>• Comments count (non-deleted comments)</li>
                        </ul>
                    </div>
                </div>
            </Card>
        </div>
    );
}

